import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouterState } from '@tanstack/react-router';
import { Popover, PopoverTrigger, PopoverContent, AppsIcon } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import {
  visibleSubApps,
  isNavigable,
  showsSoonTag,
  subAppForPath,
  type SubApp,
} from '@/entities/sub-app';

/**
 * Header app switcher for the merchant super-app. The trigger is the 2×2 "apps"
 * grid glyph next to the wordmark; the popup lists every non-hidden sub-app from
 * the `entities/sub-app` catalog — the same source the post-login welcome picker
 * reads. Sub-apps that aren't built yet render as disabled "soon" rows. The
 * currently active sub-app is highlighted. Pixel-matched to the Noor.Merchant
 * Figma (node 119:8337).
 */
export function AppSwitcher() {
  const { t } = useTranslation(['eats', 'common']);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const current = subAppForPath(pathname);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('switcher.trigger')}
          className={cn(
            'flex size-14 shrink-0 items-center justify-center rounded-full bg-muted text-foreground',
            'hover:bg-secondary-hover cursor-pointer transition-colors outline-none',
            'focus-visible:ring-[3px] focus-visible:ring-ring/40',
          )}
        >
          <AppsIcon className="size-6" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[240px] rounded-[16px] border-0 bg-popover p-[10px] shadow-[0px_-1px_24px_0px_rgba(0,0,0,0.08)]"
      >
        <ul className="flex flex-col">
          {visibleSubApps().map((app) => (
            <li key={app.id}>
              <SubAppRow
                app={app}
                label={t(`common:subApps.${app.id}`)}
                soonLabel={t('switcher.soon')}
                isCurrent={app.id === current?.id}
                onNavigate={() => setOpen(false)}
              />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

interface SubAppRowProps {
  app: SubApp;
  label: string;
  soonLabel: string;
  isCurrent: boolean;
  onNavigate: () => void;
}

/**
 * One sub-app entry. Built sub-apps are a `Link` (the active one carries the
 * `bg-muted` highlight); not-yet-built ones are inert rows with a "soon" tag.
 */
function SubAppRow({ app, label, soonLabel, isCurrent, onNavigate }: SubAppRowProps) {
  const inner = (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-border">
        <img src={app.icon} alt="" className="size-full object-cover" />
      </span>
      <span className="text-sm font-medium tracking-tight text-foreground">{label}</span>
      {showsSoonTag(app) && (
        <span className="ml-auto text-[10px] font-medium tracking-tight text-muted-foreground">
          {soonLabel}
        </span>
      )}
    </>
  );

  const rowClass = cn(
    'flex h-14 w-full items-center gap-[7px] rounded-[12px] pl-2 pr-3',
    isCurrent && 'bg-muted',
  );

  if (isNavigable(app)) {
    return (
      <Link
        to={app.path}
        onClick={onNavigate}
        aria-current={isCurrent ? 'page' : undefined}
        className={cn(
          rowClass,
          'transition-colors outline-none hover:bg-muted focus-visible:bg-muted',
        )}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      aria-disabled="true"
      className={cn(rowClass, 'cursor-default opacity-60 select-none')}
    >
      {inner}
    </div>
  );
}
